/**
 * ATLAS mode codes ordered by service quality: rail-bound above road, matching
 * how the Swiss ÖV quality classes rank them.
 */
const MODE_ORDER = [
  'TRAIN',
  'METRO',
  'TRAM',
  'CABLE_RAILWAY',
  'RACK_RAILWAY',
  'CABLE_CAR',
  'CHAIRLIFT',
  'BOAT',
  'BUS',
]

export interface AmenityTransitLike {
  modes?: string[]
  road_headway_min?: number | null
  rail_headway_min?: number | null
  rail_routes?: number | null
  /** Line names as a rider reads them off the vehicle — "4", "S11", "IC1". */
  lines?: string[]
  /** The same lines qualified by operator. An identity, never a label. */
  line_keys?: string[]
}

export interface TransitLine {
  /** Stable across operators, so two networks' line "1" stay two badges. */
  key: string
  label: string
}

/**
 * Numbers first, ascending, then named lines alphabetically with their digits
 * compared as numbers — so S2 precedes S11. Deliberately no Swiss prefix
 * ranking (S-Bahn ahead of IC and so on): the field is not indexed yet, and a
 * taxonomy invented against imagined values is worse than a rule a reader can
 * predict. Worth revisiting once real distributions exist.
 */
const compareLines = (a: TransitLine, b: TransitLine): number => {
  const left = Number(a.label)
  const right = Number(b.label)
  const leftIsNumber = a.label !== '' && Number.isFinite(left)
  const rightIsNumber = b.label !== '' && Number.isFinite(right)

  const byLabel =
    leftIsNumber && rightIsNumber
      ? left - right
      : leftIsNumber
        ? -1
        : rightIsNumber
          ? 1
          : a.label.localeCompare(b.label, undefined, { numeric: true })

  // Two operators can both run a line "1". The key breaks the tie so badge
  // order does not follow whatever order the aggregation happened to return.
  return byLabel !== 0 ? byLabel : a.key.localeCompare(b.key)
}

/**
 * The lines calling at a stop, deduplicated and ordered for display.
 *
 * `lines` and `line_keys` are parallel arrays, and either may be absent — the
 * field arrives with a future ingestion run, so every document lacks it today
 * and the row has to degrade rather than break. Pairing is positional; a label
 * with no key falls back to one derived from itself, which keeps two distinct
 * lines apart without pretending to know their operator.
 *
 * Note the array says nothing about which mode a line belongs to: Sihlquai/HB
 * returns 4, 13, 17 (trams) and 46 (a bus) in one flat list. Anything that
 * colours or groups badges by mode would be guessing.
 */
export const transitLines = (transit?: AmenityTransitLike): TransitLine[] => {
  const labels = transit?.lines
  if (!Array.isArray(labels) || labels.length === 0) return []

  const keys = Array.isArray(transit?.line_keys) ? transit.line_keys : []
  const seen = new Set<string>()
  const lines: TransitLine[] = []

  labels.forEach((raw, index) => {
    if (typeof raw !== 'string') return
    const label = raw.trim()
    if (!label) return

    const suppliedKey = keys[index]
    const key =
      typeof suppliedKey === 'string' && suppliedKey.trim()
        ? suppliedKey
        : `line:${label}`
    if (seen.has(key)) return

    seen.add(key)
    lines.push({ key, label })
  })

  return lines.sort(compareLines)
}

/**
 * How many rail lines call at the stop, or null when that is unknown.
 *
 * The pipeline counts distinct GTFS routes with a rail route type, and defaults
 * the field to 0 everywhere else — so every tram, bus, metro and boat stop
 * carries a literal zero that means "not counted here", not "no lines". Only a
 * positive count is a fact worth putting on the row.
 */
export const railLineCount = (transit?: AmenityTransitLike): number | null => {
  const routes = transit?.rail_routes
  return typeof routes === 'number' && routes > 0 ? routes : null
}

/**
 * Every mode calling at a stop, best first.
 *
 * A stop's `poi_type` carries only its highest mode, so a bus-and-tram
 * interchange rendered as "Tram Stop" and quietly dropped the bus. Around a
 * Zürich city parcel half the stops serve more than one mode.
 */
export const orderedModes = (transit?: AmenityTransitLike): string[] => {
  if (!transit?.modes?.length) return []
  return [...transit.modes].sort(
    (a, b) =>
      (MODE_ORDER.indexOf(a) === -1
        ? MODE_ORDER.length
        : MODE_ORDER.indexOf(a)) -
      (MODE_ORDER.indexOf(b) === -1 ? MODE_ORDER.length : MODE_ORDER.indexOf(b))
  )
}

/**
 * Best headway in minutes, rail preferred when both exist. Null when ATLAS has
 * neither — about a fifth of stops — so the row can omit it rather than imply
 * a service every zero minutes.
 */
/**
 * Whole minutes, because that is the precision the number actually carries:
 * ATLAS averages a day's timetable gaps, so "every 26.2 min" dresses up an
 * average as a schedule. Floored at 1 — the eight sub-minute stops in the
 * country bottom out at 0.6, and "every 0 min" would read as broken.
 */
export const formatHeadwayMinutes = (minutes: number): string =>
  String(Math.max(1, Math.round(minutes)))

export const bestHeadwayMin = (transit?: AmenityTransitLike): number | null => {
  const rail = transit?.rail_headway_min
  const road = transit?.road_headway_min
  if (typeof rail === 'number' && typeof road === 'number') {
    return Math.min(rail, road)
  }
  if (typeof rail === 'number') return rail
  if (typeof road === 'number') return road
  return null
}
